import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';

/**
 * Publisher-style dialog shell: title, optional description, body, OK/Cancel footer.
 * `dock="right"` parks the dialog along the right edge over a barely-dimmed page — for dialogs that preview live on the
 * publication (Fill Effects). `keepOpenOnOutsideClick` makes a click on the page a no-op instead of a Cancel.
 */
/** True inside a dialog that is waiting underneath another one (DialogHost renders the stack). */
export const DialogBehindContext = React.createContext(false);

export const WpDialog = ({ name, title, description, width = 'w-[560px]', children, onOk, okLabel = 'OK', onCancel, cancelLabel = 'Cancel', extraFooter, okDisabled, hideFooter, className = '', dock, keepOpenOnOutsideClick = false, overlayClassName }) => {
  const behind = React.useContext(DialogBehindContext);
  const keepOpen = keepOpenOnOutsideClick || behind; // a dialog underneath must not close when the one on top is used
  return (
  <Dialog open onOpenChange={(o) => { if (!o && !behind) onCancel && onCancel(); }}>
    <DialogContent
      className={`${width} max-w-[calc(100vw-24px)] max-h-[calc(100vh-24px)] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 bg-card text-card-foreground border border-border shadow-xl p-0 gap-0 ${dock === 'right' ? 'left-auto right-3 translate-x-0' : ''} ${behind ? 'opacity-70' : ''} ${className}`}
      overlayClassName={overlayClassName || (dock ? 'bg-black/10' : undefined)}
      onPointerDownOutside={keepOpen ? (e) => e.preventDefault() : undefined}
      onInteractOutside={keepOpen ? (e) => e.preventDefault() : undefined}
      onEscapeKeyDown={behind ? (e) => e.preventDefault() : undefined}
      data-dialog data-keep-editing data-dock={dock || undefined} data-behind={behind ? '1' : undefined} data-testid={`dialog-${name}`} onOpenAutoFocus={(e) => e.preventDefault()}
    >
      <DialogHeader className="px-5 pt-5 pb-3 text-left">
        <DialogTitle className="font-outfit text-base font-semibold">{title}</DialogTitle>
        <DialogDescription className={description ? 'text-xs text-muted-foreground' : 'sr-only'}>{description || title}</DialogDescription>
      </DialogHeader>
      <div className="px-5 pb-4 text-sm">{children}</div>
      {!hideFooter && (
        <DialogFooter className={`px-5 pb-5 flex-row flex-wrap justify-end gap-2 sm:justify-end ${dock ? 'sticky bottom-0 pt-3 border-t border-border bg-card/95 backdrop-blur-sm' : ''}`}>
          {extraFooter}
          {onCancel && <Button variant="outline" onClick={onCancel} data-testid={`dialog-${name}-cancel-button`}>{cancelLabel}</Button>}
          {onOk && <Button onClick={onOk} disabled={okDisabled} data-testid={`dialog-${name}-ok-button`}>{okLabel}</Button>}
        </DialogFooter>
      )}
    </DialogContent>
  </Dialog>
  );
};

export const Field = ({ label, children, hint, className = '' }) => (
  <label className={`flex flex-col gap-1 ${className}`}>
    <span className="text-xs text-muted-foreground">{label}</span>
    {children}
    {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
  </label>
);

export const NumberInput = ({ value, onChange, min = -99999, max = 99999, step = 1, suffix, testId, className = '', disabled }) => {
  const [local, setLocal] = React.useState(String(value ?? ''));
  React.useEffect(() => setLocal(String(value ?? '')), [value]);
  const commit = () => { const n = parseFloat(local); if (!Number.isNaN(n)) onChange(Math.max(min, Math.min(max, n))); else setLocal(String(value ?? '')); };
  return (
    <div className={`relative ${className}`}>
      <input type="text" inputMode="decimal" value={local} disabled={disabled} onChange={(e) => setLocal(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') { commit(); } if (e.key === 'ArrowUp') { e.preventDefault(); onChange(Math.min(max, (parseFloat(local) || 0) + step)); } if (e.key === 'ArrowDown') { e.preventDefault(); onChange(Math.max(min, (parseFloat(local) || 0) - step)); } }} className="h-8 w-full rounded-md border border-input bg-background px-2 pr-7 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50" data-testid={testId} />
      {suffix && <span className="absolute right-2 top-2 text-[11px] text-muted-foreground pointer-events-none">{suffix}</span>}
    </div>
  );
};

export const SelectInput = ({ value, onChange, options, testId, className = '' }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={`h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring ${className}`} data-testid={testId}>
    {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
  </select>
);

export const CheckInput = ({ checked, onChange, label, testId }) => (
  <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid={testId} />
    {label}
  </label>
);
